import { useState, useContext } from 'react';
import { useParams } from 'react-router-dom';
import {Row, Col, Card, Modal, Button, Image, Form } from 'react-bootstrap';
import { UserContext } from './UserContext';
import icUser from './assets/img/ic-user-circle.svg';
import icNumber from './assets/img/ic-number.svg';
import icCalendar from './assets/img/ic-calendar.svg';
import icDataUpdate from './assets/img/ic-table-refresh.svg';
import icImgPreview from './assets/img/ic-img.svg';
import icPhone from './assets/img/ic-smart-phone.svg';
import icGlobe from './assets/img/ic-globe.svg';
import icEmail from './assets/img/ic-mail.svg';
import { resolveMediaUrl } from './utils/mediaUrl';

const TicketBasicInfo = ({ ticket }) => {
    const { user } = useContext(UserContext);
    const { id } = useParams();

    const [showUserModal, setShowUserModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);

    // ✅ Early return to avoid accessing ticket when it's null
    if (!ticket) return null;

    const formatDateTime = (isoString) => {
        const date = new Date(isoString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${day}.${month}.${year} | ${hours}:${minutes}`;
    };

    const rawProfileImage =
        selectedUser?.profileImage ||
        selectedUser?.profileImageUrl ||   // in case backend uses a different key
        selectedUser?.image;               // fallback if called "image"

    const profileImageUrl = rawProfileImage
        ? resolveMediaUrl(rawProfileImage)
        : icImgPreview;

    const companyTitle =
        selectedUser?.company?.title || // populated company
        selectedUser?.companyName ||    // legacy / fallback
        'N/A';
    
    return (
        <>

        
            <Card className="mb-4 bg">
                <Card.Body>
                    <h3>1. Basic information</h3>
                    <hr />
                    <Row>
                        <Col md={3}>
                            <div className="icon-text">
                                <span className="icon" aria-hidden="true">
                                    <Image
                                        src={icNumber}
                                        alt="Ticket ID number"
                                    />
                                </span>
                                <span className="text">
                                    Ticket ID number
                                    <br />
                                    <strong>#{ticket.ticketId}</strong>
                                </span>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="icon-text">
                                <span className="icon" aria-hidden="true">
                                    <Image
                                        src={icUser}
                                        alt="Ticket created"
                                    />
                                </span>
                                <span className="text">
                                    Ticket created
                                    <br />
                                    <span
                                        className="me-2 link"
                                        role="button"
                                        onClick={() => {
                                            setSelectedUser(ticket.createdBy);
                                            setShowUserModal(true);
                                        }}
                                        >
                                        {ticket.createdBy?.firstName || ticket.createdBy?.email || 'Unknown user'}
                                    </span>
                                    <strong>{formatDateTime(ticket.createdAt)}</strong>
                                </span>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="icon-text">
                                <span className="icon" aria-hidden="true">
                                    <Image
                                        src={icDataUpdate}
                                        alt="Last Updated"
                                    />
                                </span>
                                <span className="text">
                                    Last Updated
                                    <br />
                                    <span
                                        role="button"
                                        className="me-2 link"
                                        onClick={() => {
                                            setSelectedUser(ticket.updatedBy);
                                            setShowUserModal(true);
                                        }}
                                        >
                                        {ticket.updatedBy?.firstName || ticket.updatedBy?.email || 'Unknown user'}
                                    </span>
                                    <strong>{formatDateTime(ticket.updatedAt)}</strong>
                                </span>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="icon-text">
                                <span className="icon" aria-hidden="true">
                                    <Image
                                        src={icCalendar}
                                        alt="Open since"
                                    />
                                </span>
                                <span className="text">
                                    Open since
                                    <br />
                                    <span><strong>09:30</strong></span>
                                </span>
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* User Modal */}
            <Modal className="with-form" show={showUserModal} onHide={() => setShowUserModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title><h1>Ticket creator details</h1></Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedUser ? (
                        <>
                            <Card className="border-0 shadow-none">
                                <Card.Body className="border-0 p-0">
                                    <Row className="m-0 mb-2 modal-info border-0">
                                        <Col md={12} className="d-flex">
                                            <div className="upload-image-wrapper">
                                                <Form.Group controlId="profileImage" className="mb-3 text-center upload-image-wrapper">
                                                    <div className="upload-preview not-editing">
                                                        <Image
                                                            src={profileImageUrl} // ✅ uses selectedUser.profileImage with fallback
                                                            alt="Profile Preview"
                                                            roundedCircle
                                                            className="preview-image"
                                                            width="136"
                                                            height="136"
                                                        />
                                                    </div>
                                                </Form.Group>
                                            </div>
                                            <div className="ms-3">
                                                <h2>{selectedUser.firstName} {selectedUser.familyName}</h2>
                                                <p>
                                                    {selectedUser.gender} <span className="dot"></span> {selectedUser.role}
                                                    <span className="dot"></span>
                                                </p>
                                                <div className="status-wrapper">
                                                    <p>
                                                        <span 
                                                            className={`badge ${selectedUser.activityStatus ? 'bg-active' : 'bg-inactive'}`}
                                                        >
                                                            {selectedUser.activityStatus ? 'active' : 'inactive'}
                                                        </span>
                                                    </p>
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col>
                                            <hr />
                                        </Col>
                                    </Row>
                                    <Row className="mb-3">
                                        <Col md={6}>
                                            <div className="icon-text">
                                                <span className="icon" aria-hidden="true">
                                                    <Image src={icPhone} alt="Phone" />
                                                </span>
                                                <span className="text text-dark">
                                                    Phone
                                                    <br />
                                                    <strong>
                                                        {selectedUser.phone || 'N/A'}
                                                    </strong>
                                                </span>
                                            </div>
                                        </Col>
                                        <Col md={6}>
                                            <div className="icon-text">
                                                <span className="icon" aria-hidden="true">
                                                    <Image src={icGlobe} alt="Country" />
                                                </span>
                                                <span className="text text-dark">
                                                    Country
                                                    <br />
                                                    <strong>
                                                        {selectedUser.country}
                                                    </strong>
                                                </span>
                                            </div>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md={6}>
                                            <div className="icon-text">
                                                <span className="icon" aria-hidden="true">
                                                    <Image src={icEmail} alt="Email Address" />
                                                </span>
                                                <span className="text text-dark">
                                                    Email Address
                                                    <br />
                                                    <strong className="text-dark">
                                                        {selectedUser.email}
                                                    </strong>
                                                </span>
                                            </div>
                                        </Col>
                                        <Col md={6}>
                                            <div className="icon-text">
                                                <span className="icon" aria-hidden="true">
                                                    <Image src={icCalendar} alt="Date of birth" />
                                                </span>
                                                <span className="text text-dark">
                                                    Date of birth
                                                    <br />
                                                    <strong>
                                                        {selectedUser.birthDate
                                                            ? new Date(selectedUser.birthDate).toLocaleDateString('sr-RS')
                                                            : 'N/A'
                                                        }
                                                    </strong>
                                                </span>
                                            </div>
                                        </Col>
                                        <Col>
                                            <hr className="pt-4" />
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col>
                                            <div className="icon-text">
                                                <span className="icon" aria-hidden="true">
                                                    <Image src={icCalendar} alt="Company" />
                                                </span>
                                                <span className="text text-dark">
                                                    Company
                                                    <br />
                                                    <strong>{companyTitle}</strong>
                                                </span>
                                            </div>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col>
                                            <hr className="pt-4" />
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>

                        </>
                    ) : (
                        <p>No user details available.</p>
                    )}
                    <div className="justify-content-center d-flex">
                        <Button variant="outline-secondary" className="me-3" onClick={() => setShowUserModal(false)}>
                            Close
                        </Button>
                        {/* <Button variant="primary">
                            Start Chat
                        </Button> */}
                    </div>
                </Modal.Body>
            </Modal>
        </>
    );
};

export default TicketBasicInfo;
